
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import type { PricingTier } from "@/config/pricing";

export const useSubscription = () => {
  const { toast } = useToast();

  const handleSubscribe = async (tier: PricingTier) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        toast({
          title: "Authentication required",
          description: "Please sign in to subscribe to a plan",
          variant: "destructive",
        });
        return;
      }

      // Handle custom pricing for B2B tiers
      if (tier.customPricing) {
        // Store the request in the database or send an email notification
        // For now, just show a toast notification
        toast({
          title: "Custom pricing request received",
          description: "Our team will contact you within 24 hours to discuss your specific needs.",
        });
        return;
      }

      // Handle Free tier subscription
      if (tier.name === "Starter") {
        // Calculate trial end date (7 days from now)
        const trialEndDate = new Date();
        trialEndDate.setDate(trialEndDate.getDate() + 7);

        // First check if profile exists
        const { data: existingProfile, error: profileError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', session.user.id)
          .maybeSingle();

        if (profileError) {
          console.error('Profile fetch error:', profileError);
          toast({
            title: "Error",
            description: "Failed to fetch profile. Please try again.",
            variant: "destructive",
          });
          return;
        }

        if (!existingProfile) {
          toast({
            title: "Error",
            description: "Profile not found. Please try signing out and back in.",
            variant: "destructive",
          });
          return;
        }

        const { error: updateError } = await supabase
          .from('profiles')
          .update({ 
            paid_account: false,
            trial_end_date: trialEndDate.toISOString(),
            updated_at: new Date().toISOString(),
          })
          .eq('id', session.user.id);

        if (updateError) {
          console.error('Free tier update error:', updateError);
          toast({
            title: "Error",
            description: "Failed to update to free tier. Please try again.",
            variant: "destructive",
          });
          return;
        }

        toast({
          title: "Success",
          description: "You're now on the Starter plan with a 7-day trial of Pro features!",
        });
        return;
      }

      // Handle paid tiers (Pro and Enterprise)
      if (!tier.priceId) {
        toast({
          title: "Error",
          description: "Invalid subscription tier",
          variant: "destructive",
        });
        return;
      }

      console.log('Creating checkout session for tier:', tier.name, 'with price ID:', tier.priceId);
      
      const { data, error } = await supabase.functions.invoke('create-checkout-session', {
        body: { priceId: tier.priceId },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        console.error('Checkout error:', error);
        throw error;
      }
      
      if (data.error) {
        console.error('Checkout data error:', data.error);
        throw new Error(data.error);
      }

      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Subscription error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to process subscription",
        variant: "destructive",
      });
    }
  };

  return { handleSubscribe };
};
